import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  forwardRef,
} from "react";
import { motion } from "framer-motion";
import { Primitive, PrimitiveProps } from "../primitive/primitive";

// --- Avatar Context ---

interface AvatarContextProps {
  isImageLoaded: boolean;
  hasImageError: boolean;
  hasAvatarImage: boolean;
  markImageLoaded: () => void;
  markImageError: () => void;
  markHasAvatarImage: () => void;
}

const AvatarContext = createContext<AvatarContextProps | undefined>(undefined);

const useAvatarContext = (): AvatarContextProps => {
  const context = useContext(AvatarContext);
  if (!context) {
    throw new Error("Avatar components must be used within an AvatarProvider");
  }
  return context;
};

const AvatarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);
  const [hasAvatarImage, setHasAvatarImage] = useState(false);

  const markImageLoaded = () => setIsImageLoaded(true);
  const markImageError = () => setHasImageError(true);
  const markHasAvatarImage = () => setHasAvatarImage(true);

  return (
    <AvatarContext.Provider
      value={{
        isImageLoaded,
        hasImageError,
        hasAvatarImage,
        markImageLoaded,
        markImageError,
        markHasAvatarImage,
      }}
    >
      {children}
    </AvatarContext.Provider>
  );
};

// --- Avatar Component ---

interface AvatarProps extends PrimitiveProps<"div"> {}

const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  ({ children, ...props }, ref) => {
    return (
      <AvatarProvider>
        <Primitive as="div" ref={ref} {...props}>
          {children}
        </Primitive>
      </AvatarProvider>
    );
  },
);

Avatar.displayName = "Avatar";

// --- AvatarImage Component ---

interface AvatarImageProps extends Omit<PrimitiveProps<"img">, "children"> {
  src: string;
  alt?: string;
  onLoadingStatusChange?: (
    status: "idle" | "loading" | "loaded" | "error",
  ) => void;
}

const AvatarImage = forwardRef<HTMLImageElement, AvatarImageProps>(
  ({ src, alt = "Avatar", onLoadingStatusChange, ...props }, ref) => {
    const {
      markImageLoaded,
      markImageError,
      hasImageError,
      markHasAvatarImage,
    } = useAvatarContext();

    useEffect(() => {
      markHasAvatarImage();
      if (onLoadingStatusChange) onLoadingStatusChange("loading");

      const img = new Image();
      img.src = src;
      img.onload = () => {
        markImageLoaded();
        if (onLoadingStatusChange) onLoadingStatusChange("loaded");
      };
      img.onerror = () => {
        markImageError();
        if (onLoadingStatusChange) onLoadingStatusChange("error");
      };

      return () => {
        if (onLoadingStatusChange) onLoadingStatusChange("idle");
      };
    }, [
      src,
      markImageLoaded,
      markImageError,
      markHasAvatarImage,
      onLoadingStatusChange,
    ]);

    if (hasImageError) return null;

    return (
      <motion.img
        src={src}
        alt={alt}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        ref={ref}
        {...props}
      />
    );
  },
);

AvatarImage.displayName = "AvatarImage";

// --- AvatarFallback Component ---

interface AvatarFallbackProps extends PrimitiveProps<"div"> {
  children: React.ReactNode;
  delay?: number;
}

const AvatarFallback = forwardRef<HTMLDivElement, AvatarFallbackProps>(
  ({ children, delay = 0, ...props }, ref) => {
    const { isImageLoaded, hasImageError, hasAvatarImage } = useAvatarContext();
    const [showFallback, setShowFallback] = useState<boolean>(false);

    useEffect(() => {
      let timer: NodeJS.Timeout;

      const shouldShowFallback =
        !hasAvatarImage || hasImageError || (!isImageLoaded && hasAvatarImage);

      if (shouldShowFallback) {
        if (delay > 0) {
          timer = setTimeout(() => setShowFallback(true), delay);
        } else {
          setShowFallback(true);
        }
      } else {
        setShowFallback(false);
      }

      return () => {
        if (timer) clearTimeout(timer);
      };
    }, [isImageLoaded, hasImageError, hasAvatarImage, delay]);

    if (!showFallback) {
      return null;
    }

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        ref={ref}
        {...props}
      >
        {children}
      </motion.div>
    );
  },
);

AvatarFallback.displayName = "AvatarFallback";

// --- Export ---

export {
  Avatar,
  AvatarImage,
  AvatarFallback,
  type AvatarProps,
  type AvatarImageProps,
  type AvatarFallbackProps,
};
